import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import crmService from "../../crmConfig.json";
import { crmAxios } from "../../app/crmApi";
import { errorToast, successToast } from "../../helpers/toaster";

const initialState = {
    destinations: [],
    destinationsPagination: { page: 1, limit: 20, total: 0, totalPages: 0 },
    activeDestinations: [],
    hotels: [],
    hotelsPagination: { page: 1, limit: 20, total: 0, totalPages: 0 },
    activeHotels: [],
    categories: [],
    packages: [],
    packagesPagination: { page: 1, limit: 20, total: 0, totalPages: 0 },
    selectedPackage: null,
    loading: false,
    saving: false,
};

// --- Destinations ---
export const fetchDestinationsAsync = createAsyncThunk(
    "crmCatalog/fetchDestinations",
    async (params, { rejectWithValue }) => {
        try {
            const res = await crmAxios.get(crmService.destinations, { params });
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

export const fetchActiveDestinationsAsync = createAsyncThunk(
    "crmCatalog/fetchActiveDestinations",
    async (_, { rejectWithValue }) => {
        try {
            const res = await crmAxios.get(crmService.activeDestinations);
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

export const saveDestinationAsync = createAsyncThunk(
    "crmCatalog/saveDestination",
    async ({ id, formData }, { rejectWithValue }) => {
        try {
            const res = id
                ? await crmAxios.put(`${crmService.destinations}/${id}`, formData, {
                      headers: { "Content-Type": "multipart/form-data" },
                  })
                : await crmAxios.post(crmService.destinations, formData, {
                      headers: { "Content-Type": "multipart/form-data" },
                  });
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

export const deleteDestinationAsync = createAsyncThunk(
    "crmCatalog/deleteDestination",
    async (id, { rejectWithValue }) => {
        try {
            const res = await crmAxios.delete(`${crmService.destinations}/${id}`);
            return { ...res.data, id };
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

// --- Hotels ---
export const fetchHotelsAsync = createAsyncThunk(
    "crmCatalog/fetchHotels",
    async (params, { rejectWithValue }) => {
        try {
            const res = await crmAxios.get(crmService.hotels, { params });
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

export const fetchActiveHotelsAsync = createAsyncThunk(
    "crmCatalog/fetchActiveHotels",
    async (_, { rejectWithValue }) => {
        try {
            const res = await crmAxios.get(crmService.activeHotels);
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

export const saveHotelAsync = createAsyncThunk(
    "crmCatalog/saveHotel",
    async ({ id, formData }, { rejectWithValue }) => {
        try {
            const res = id
                ? await crmAxios.put(`${crmService.hotels}/${id}`, formData, {
                      headers: { "Content-Type": "multipart/form-data" },
                  })
                : await crmAxios.post(crmService.hotels, formData, {
                      headers: { "Content-Type": "multipart/form-data" },
                  });
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

export const deleteHotelAsync = createAsyncThunk(
    "crmCatalog/deleteHotel",
    async (id, { rejectWithValue }) => {
        try {
            const res = await crmAxios.delete(`${crmService.hotels}/${id}`);
            return { ...res.data, id };
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

// --- Package categories ---
export const fetchCategoriesAsync = createAsyncThunk(
    "crmCatalog/fetchCategories",
    async (_, { rejectWithValue }) => {
        try {
            const res = await crmAxios.get(crmService.packageCategories);
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

export const saveCategoryAsync = createAsyncThunk(
    "crmCatalog/saveCategory",
    async ({ id, payload }, { rejectWithValue }) => {
        try {
            const res = id
                ? await crmAxios.put(`${crmService.packageCategories}/${id}`, payload)
                : await crmAxios.post(crmService.packageCategories, payload);
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

export const deleteCategoryAsync = createAsyncThunk(
    "crmCatalog/deleteCategory",
    async (id, { rejectWithValue }) => {
        try {
            const res = await crmAxios.delete(`${crmService.packageCategories}/${id}`);
            return { ...res.data, id };
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

// --- Packages ---
export const fetchPackagesAsync = createAsyncThunk(
    "crmCatalog/fetchPackages",
    async (params, { rejectWithValue }) => {
        try {
            const res = await crmAxios.get(crmService.packages, { params });
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

export const fetchPackageByIdAsync = createAsyncThunk(
    "crmCatalog/fetchPackageById",
    async (id, { rejectWithValue }) => {
        try {
            const res = await crmAxios.get(`${crmService.packages}/${id}`);
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

export const savePackageAsync = createAsyncThunk(
    "crmCatalog/savePackage",
    async ({ id, formData }, { rejectWithValue }) => {
        try {
            const res = id
                ? await crmAxios.put(`${crmService.packages}/${id}`, formData, {
                      headers: { "Content-Type": "multipart/form-data" },
                  })
                : await crmAxios.post(crmService.packages, formData, {
                      headers: { "Content-Type": "multipart/form-data" },
                  });
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

export const deletePackageAsync = createAsyncThunk(
    "crmCatalog/deletePackage",
    async (id, { rejectWithValue }) => {
        try {
            const res = await crmAxios.delete(`${crmService.packages}/${id}`);
            return { ...res.data, id };
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

const packageSubResource = (name, method, suffix, isMultipart) =>
    createAsyncThunk(name, async ({ id, payload }, { rejectWithValue }) => {
        try {
            const url = `${crmService.packages}/${id}${suffix}`;
            const res = isMultipart
                ? await crmAxios[method](url, payload, {
                      headers: { "Content-Type": "multipart/form-data" },
                  })
                : await crmAxios[method](url, payload);
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    });

export const setPackageDestinationsAsync = packageSubResource(
    "crmCatalog/setPackageDestinations",
    "put",
    "/destinations"
);
export const setPackageHotelsAsync = packageSubResource(
    "crmCatalog/setPackageHotels",
    "put",
    "/hotels"
);
export const setPackageItineraryAsync = packageSubResource(
    "crmCatalog/setPackageItinerary",
    "put",
    "/itinerary"
);
export const addPackageGalleryImagesAsync = packageSubResource(
    "crmCatalog/addPackageGalleryImages",
    "post",
    "/gallery",
    true
);
export const setPackageInclusionsAsync = packageSubResource(
    "crmCatalog/setPackageInclusions",
    "put",
    "/inclusions"
);
export const setPackageExclusionsAsync = packageSubResource(
    "crmCatalog/setPackageExclusions",
    "put",
    "/exclusions"
);

export const removePackageGalleryImageAsync = createAsyncThunk(
    "crmCatalog/removePackageGalleryImage",
    async ({ id, imageId }, { rejectWithValue }) => {
        try {
            const res = await crmAxios.delete(
                `${crmService.packages}/${id}/gallery/${imageId}`
            );
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

const subResourceThunks = [
    setPackageDestinationsAsync,
    setPackageHotelsAsync,
    setPackageItineraryAsync,
    addPackageGalleryImagesAsync,
    setPackageInclusionsAsync,
    setPackageExclusionsAsync,
    removePackageGalleryImageAsync,
];
const isSubResource = (phase) => (action) =>
    subResourceThunks.some((t) => action.type === t[phase].type);

const crmCatalogSlice = createSlice({
    name: "crmCatalog",
    initialState,
    reducers: {
        clearSelectedPackage: (state) => {
            state.selectedPackage = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Destinations
            .addCase(fetchDestinationsAsync.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchDestinationsAsync.fulfilled, (state, { payload }) => {
                state.loading = false;
                if (payload?.success) {
                    state.destinations = payload.data.destinations;
                    state.destinationsPagination = payload.data.pagination;
                }
            })
            .addCase(fetchDestinationsAsync.rejected, (state) => {
                state.loading = false;
            })
            .addCase(fetchActiveDestinationsAsync.fulfilled, (state, { payload }) => {
                if (payload?.success) state.activeDestinations = payload.data;
            })
            .addCase(saveDestinationAsync.pending, (state) => {
                state.saving = true;
            })
            .addCase(saveDestinationAsync.fulfilled, (state, { payload }) => {
                state.saving = false;
                if (payload?.success) successToast(payload.message);
                else errorToast(payload?.message || "Save failed");
            })
            .addCase(saveDestinationAsync.rejected, (state, { payload }) => {
                state.saving = false;
                errorToast(payload?.message || "Save failed");
            })
            .addCase(deleteDestinationAsync.fulfilled, (state, { payload }) => {
                if (payload?.success) {
                    successToast(payload.message);
                    state.destinations = state.destinations.filter(
                        (d) => d.id !== payload.id
                    );
                } else {
                    errorToast(payload?.message || "Delete failed");
                }
            })
            // Hotels
            .addCase(fetchHotelsAsync.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchHotelsAsync.fulfilled, (state, { payload }) => {
                state.loading = false;
                if (payload?.success) {
                    state.hotels = payload.data.hotels;
                    state.hotelsPagination = payload.data.pagination;
                }
            })
            .addCase(fetchHotelsAsync.rejected, (state) => {
                state.loading = false;
            })
            .addCase(fetchActiveHotelsAsync.fulfilled, (state, { payload }) => {
                if (payload?.success) state.activeHotels = payload.data;
            })
            .addCase(saveHotelAsync.pending, (state) => {
                state.saving = true;
            })
            .addCase(saveHotelAsync.fulfilled, (state, { payload }) => {
                state.saving = false;
                if (payload?.success) successToast(payload.message);
                else errorToast(payload?.message || "Save failed");
            })
            .addCase(saveHotelAsync.rejected, (state, { payload }) => {
                state.saving = false;
                errorToast(payload?.message || "Save failed");
            })
            .addCase(deleteHotelAsync.fulfilled, (state, { payload }) => {
                if (payload?.success) {
                    successToast(payload.message);
                    state.hotels = state.hotels.filter((h) => h.id !== payload.id);
                } else {
                    errorToast(payload?.message || "Delete failed");
                }
            })
            // Categories
            .addCase(fetchCategoriesAsync.fulfilled, (state, { payload }) => {
                if (payload?.success) state.categories = payload.data;
            })
            .addCase(saveCategoryAsync.fulfilled, (state, { payload }) => {
                if (payload?.success) successToast(payload.message);
                else errorToast(payload?.message || "Save failed");
            })
            .addCase(saveCategoryAsync.rejected, (state, { payload }) => {
                errorToast(payload?.message || "Save failed");
            })
            .addCase(deleteCategoryAsync.fulfilled, (state, { payload }) => {
                if (payload?.success) {
                    successToast(payload.message);
                    state.categories = state.categories.filter(
                        (c) => c.id !== payload.id
                    );
                } else {
                    errorToast(payload?.message || "Delete failed");
                }
            })
            // Packages
            .addCase(fetchPackagesAsync.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchPackagesAsync.fulfilled, (state, { payload }) => {
                state.loading = false;
                if (payload?.success) {
                    state.packages = payload.data.packages;
                    state.packagesPagination = payload.data.pagination;
                }
            })
            .addCase(fetchPackagesAsync.rejected, (state) => {
                state.loading = false;
            })
            .addCase(fetchPackageByIdAsync.fulfilled, (state, { payload }) => {
                if (payload?.success) state.selectedPackage = payload.data;
            })
            .addCase(savePackageAsync.pending, (state) => {
                state.saving = true;
            })
            .addCase(savePackageAsync.fulfilled, (state, { payload }) => {
                state.saving = false;
                if (payload?.success) {
                    successToast(payload.message);
                    state.selectedPackage = payload.data;
                } else {
                    errorToast(payload?.message || "Save failed");
                }
            })
            .addCase(savePackageAsync.rejected, (state, { payload }) => {
                state.saving = false;
                errorToast(payload?.message || "Save failed");
            })
            .addCase(deletePackageAsync.fulfilled, (state, { payload }) => {
                if (payload?.success) {
                    successToast(payload.message);
                    state.packages = state.packages.filter((p) => p.id !== payload.id);
                } else {
                    errorToast(payload?.message || "Delete failed");
                }
            })
            // Sub-resources
            .addMatcher(isSubResource("pending"), (state) => {
                state.saving = true;
            })
            .addMatcher(isSubResource("fulfilled"), (state, { payload }) => {
                state.saving = false;
                if (payload?.success) {
                    successToast(payload.message);
                    state.selectedPackage = payload.data;
                } else {
                    errorToast(payload?.message || "Save failed");
                }
            })
            .addMatcher(isSubResource("rejected"), (state, { payload }) => {
                state.saving = false;
                errorToast(payload?.message || "Save failed");
            });
    },
});

export const { clearSelectedPackage } = crmCatalogSlice.actions;

export const selectDestinations = (state) => state.crmCatalog.destinations;
export const selectDestinationsPagination = (state) =>
    state.crmCatalog.destinationsPagination;
export const selectActiveDestinations = (state) => state.crmCatalog.activeDestinations;
export const selectHotels = (state) => state.crmCatalog.hotels;
export const selectHotelsPagination = (state) => state.crmCatalog.hotelsPagination;
export const selectActiveHotels = (state) => state.crmCatalog.activeHotels;
export const selectCategories = (state) => state.crmCatalog.categories;
export const selectPackages = (state) => state.crmCatalog.packages;
export const selectPackagesPagination = (state) => state.crmCatalog.packagesPagination;
export const selectSelectedPackage = (state) => state.crmCatalog.selectedPackage;
export const selectCatalogLoading = (state) => state.crmCatalog.loading;
export const selectCatalogSaving = (state) => state.crmCatalog.saving;

export default crmCatalogSlice.reducer;
